import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RefreshCw, Lock, Mail } from "lucide-react";
import axios from "axios";
import { PasswordInput } from "../components/shared/PasswordInput";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { LOJACK_LOGO, API } from "../config";
import { TelcaVoIPBadge } from "../components/shared/TelcaVoIPBadge";
import { MFAChallengeModal } from "../components/shared/MFA";

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);  // {token, method}
  // ── "Password dimenticata?" modal (Mara CR P2, 17/06/2026) ──
  // Self-service flow: enter email, submit, receive reset link.
  // Backend always returns the same generic 200 (anti-enumeration),
  // so the only UX state we track is "submitted" → show generic
  // confirmation message regardless of whether the email exists.
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSubmitted, setForgotSubmitted] = useState(false);
  const { login, completeMfaLogin, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Fabrizio HOTFIX 26/02/2026 — role="salesforce" (Motta) owns the CRM
  // Portal, not the Admin Dashboard. Default her landing to /salesforce
  // so she never sees /admin widgets that 403 for her role.
  const defaultLanding = (u) => (u?.role === "salesforce" ? "/salesforce" : "/admin");

  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || defaultLanding(user);
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  // If the previous session was terminated because the account was opened on
  // another device, surface a clear message once on the login screen.
  useEffect(() => {
    try {
      const reason = window.sessionStorage.getItem("lojack_logout_reason");
      if (reason) {
        toast.error(reason, { duration: 6000 });
        window.sessionStorage.removeItem("lojack_logout_reason");
      }
    } catch {/* ignore */}
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.mfa_required) {
        // Show the MFA challenge modal. The actual session JWT will be
        // installed by completeMfaLogin() once the user types a valid code.
        setMfaChallenge({ token: res.mfa_challenge_token, method: res.mfa_method });
        return;
      }
      toast.success('Login effettuato con successo');
      const from = location.state?.from?.pathname || defaultLanding(res?.user);
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  const onMfaSuccess = (payload) => {
    const u = completeMfaLogin(payload);
    setMfaChallenge(null);
    toast.success('Login effettuato con successo');
    const from = location.state?.from?.pathname || defaultLanding(u);
    navigate(from, { replace: true });
  };

  return (
    <div className="min-h-screen bg-lojack-black flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex flex-col items-center justify-center gap-3 mb-6">
            <img src={LOJACK_LOGO} alt="LoJack Tracker" className="h-14" data-testid="admin-login-lojack-logo" />
            <img src="/securewallet_logo_white.webp" alt="Secure Wallet" className="h-8" data-testid="admin-login-securewallet-logo" />
          </div>
          <h1 className="text-2xl font-bold text-white">Accesso Admin</h1>
        </div>

        <div className="wallet-card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-lojack-red focus:outline-none"
                placeholder="admin@lojack.it" required data-testid="login-email" />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Password</label>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 pr-12 text-white focus:border-lojack-red focus:outline-none"
                placeholder="********" required data-testid="login-password" />
            </div>
            <button type="submit" disabled={loading}
              className="w-full btn-lojack flex items-center justify-center gap-2 py-4" data-testid="login-submit">
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <><Lock size={18} />Accedi</>}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => { setForgotEmail(email); setForgotOpen(true); setForgotSubmitted(false); }}
                className="text-sm text-gray-400 hover:text-lojack-red transition underline-offset-2 hover:underline"
                data-testid="forgot-password-link"
              >
                Password dimenticata?
              </button>
            </div>
          </form>
        </div>

        <div className="text-center mt-8">
          <TelcaVoIPBadge />
        </div>
      </div>

      {/* Forgot password modal — calls POST /api/auth/admin/forgot-password.
          The endpoint always returns 200 with a generic message
          (anti-enumeration), so we treat "submitted" as terminal
          regardless of whether the email exists in admin_users. */}
      {forgotOpen && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setForgotOpen(false)}
          data-testid="forgot-modal-overlay"
        >
          <div
            className="bg-[#1A1A1A] rounded-2xl w-full max-w-md border border-gray-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="forgot-modal"
          >
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Mail size={18} className="text-lojack-red" />
                Reset password
              </h3>
              <p className="text-gray-400 text-sm mt-1">
                Riceverai un&apos;email con un link valido 60 minuti per reimpostare la password.
              </p>
            </div>
            <div className="p-6">
              {forgotSubmitted ? (
                <div className="space-y-4">
                  <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-lg p-4 text-sm text-emerald-200" data-testid="forgot-submitted-banner">
                    Se l&apos;indirizzo è registrato come amministratore, riceverai a breve un&apos;email con il link per reimpostare la password. Controlla anche la cartella spam.
                  </div>
                  <button
                    onClick={() => setForgotOpen(false)}
                    className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg"
                    data-testid="forgot-close-btn"
                  >
                    Chiudi
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!forgotEmail.trim()) return;
                    setForgotSending(true);
                    try {
                      await axios.post(`${API}/auth/admin/forgot-password`, { email: forgotEmail.trim() });
                      setForgotSubmitted(true);
                    } catch (err) {
                      // Even on backend errors, fall back to generic UX
                      // to avoid leaking signal. Surface only critical
                      // 5xx as a toast.
                      if (err.response?.status >= 500) {
                        toast.error("Errore temporaneo del servizio. Riprova fra qualche minuto.");
                      } else {
                        setForgotSubmitted(true);
                      }
                    } finally {
                      setForgotSending(false);
                    }
                  }}
                  className="space-y-4"
                  data-testid="forgot-form"
                >
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Email</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      required
                      autoFocus
                      placeholder="admin@lojack.it"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-lojack-red focus:outline-none"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setForgotOpen(false)}
                      className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg"
                      data-testid="forgot-cancel-btn"
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      disabled={forgotSending || !forgotEmail.trim()}
                      className="flex-1 px-4 py-2.5 bg-lojack-red hover:bg-lojack-red-hover text-white text-sm rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="forgot-submit-btn"
                    >
                      {forgotSending ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Invia link"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
      {mfaChallenge && (
        <MFAChallengeModal
          challengeToken={mfaChallenge.token}
          method={mfaChallenge.method}
          onSuccess={onMfaSuccess}
          onCancel={() => setMfaChallenge(null)}
        />
      )}
    </div>
  );
};

export default LoginPage;
